import type { IVoucher, VoucherStatus, VoucherType } from '@/entities/voucher'
import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from '@/shared/libs/generate-path'
import { useTenantsQuery } from '@/entities/tenant'
import { useVouchersQuery } from '@/entities/voucher'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { VoucherListTable } from '@/features/voucher/voucher-list-table'
import { ALL_FILTER_VALUE } from '@/shared/constants/filters'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

const VOUCHER_STATUS_FILTER = {
  ALL: ALL_FILTER_VALUE,
  AVAILABLE: 'AVAILABLE',
  UNAVAILABLE: 'UNAVAILABLE',
} as const

const VOUCHER_TYPE_FILTER = {
  ALL: ALL_FILTER_VALUE,
  TOUR: 'TOUR',
  POI: 'POI',
} as const

type StatusFilter = (typeof VOUCHER_STATUS_FILTER)[keyof typeof VOUCHER_STATUS_FILTER]
type TypeFilter = (typeof VOUCHER_TYPE_FILTER)[keyof typeof VOUCHER_TYPE_FILTER]

export function VoucherListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId = '', isSuperAdmin, isTenantAdmin, setActiveTenantId } = useAdminScope()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(VOUCHER_STATUS_FILTER.ALL)
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(VOUCHER_TYPE_FILTER.ALL)

  const { data: tenantsResponse } = useTenantsQuery(
    { page: 1, pageSize: DEFAULT_PAGE_SIZE, isActive: true },
    { enabled: isSuperAdmin },
  )

  const tenantOptions = useMemo(
    () =>
      (tenantsResponse?.data ?? [])
        .filter((t) => t.name !== 'Default')
        .map((t) => ({ label: t.name, value: t.id })),
    [tenantsResponse?.data],
  )

  const currentTenantId = useMemo(() => {
    if (isTenantAdmin) return activeTenantId
    if (activeTenantId && tenantOptions.some((opt) => opt.value === activeTenantId))
      return activeTenantId
    return tenantOptions[0]?.value ?? ''
  }, [activeTenantId, isTenantAdmin, tenantOptions])

  const currentTenantName = useMemo(
    () => tenantOptions.find((item) => item.value === currentTenantId)?.label ?? '',
    [currentTenantId, tenantOptions],
  )
  const isTenantResolving = isSuperAdmin && !currentTenantId

  useLayoutEffect(() => {
    if (isTenantAdmin || !currentTenantId || currentTenantId === activeTenantId) return
    setActiveTenantId(currentTenantId)
  }, [activeTenantId, currentTenantId, isTenantAdmin, setActiveTenantId])

  const { data, isLoading } = useVouchersQuery(
    currentTenantId,
    {
      current: page,
      pageSize,
      ...(statusFilter !== VOUCHER_STATUS_FILTER.ALL
        ? { status: statusFilter as VoucherStatus }
        : {}),
      ...(typeFilter !== VOUCHER_TYPE_FILTER.ALL ? { type: typeFilter as VoucherType } : {}),
    },
    { enabled: Boolean(currentTenantId) },
  )

  const vouchers = useMemo(() => {
    const items: IVoucher[] = data?.data ?? []
    const keyword = searchTerm.trim().toLowerCase()
    if (!keyword) return items
    return items.filter(
      (v) => v.code.toLowerCase().includes(keyword) || v.name.toLowerCase().includes(keyword),
    )
  }, [data?.data, searchTerm])

  const hasClientFilters = Boolean(searchTerm.trim())
  const total = hasClientFilters ? vouchers.length : (data?.total ?? 0)

  const activeFilterCount = [
    statusFilter !== VOUCHER_STATUS_FILTER.ALL ? statusFilter : '',
    typeFilter !== VOUCHER_TYPE_FILTER.ALL ? typeFilter : '',
  ].filter(Boolean).length

  function resetFilters() {
    setStatusFilter(VOUCHER_STATUS_FILTER.ALL)
    setTypeFilter(VOUCHER_TYPE_FILTER.ALL)
    setPage(1)
  }

  function handleEdit(voucher: IVoucher) {
    if (!currentTenantId) return
    void navigate({
      href: scopedAdminPath(
        generatePath('/tenant/:tenantId/vouchers/:voucherId/edit', {
          tenantId: currentTenantId,
          voucherId: voucher.id,
        }),
      ),
    })
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('voucher.title')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(currentTenantName
            ? [
                {
                  label: currentTenantName,
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId', { tenantId: currentTenantId }),
                  ),
                },
              ]
            : []),
          { label: t('sidebar.vouchers') },
        ]}
        actions={
          <Button
            disabled={!currentTenantId}
            onClick={() => {
              void navigate({
                href: scopedAdminPath(
                  generatePath('/tenant/:tenantId/vouchers/create', { tenantId: currentTenantId }),
                ),
              })
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t('voucher.create')}
          </Button>
        }
      />

      <DataTableToolbar>
        {isSuperAdmin && (
          <DataTableFilterField label={t('sidebar.tenants')}>
            <Select
              value={currentTenantId || ''}
              disabled={isTenantResolving}
              onValueChange={(value) => {
                setActiveTenantId(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-60">
                <SelectValue placeholder={t('sidebar.tenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>
        )}

        <DataTableFilterField label={t('common.search')} className="min-w-[240px]">
          <TenantSearchBar
            className="w-64"
            onSearch={(term) => {
              setSearchTerm(term)
              setPage(1)
            }}
          />
        </DataTableFilterField>

        <div className={isTenantResolving ? 'pointer-events-none self-end opacity-60' : 'self-end'}>
          <FilterPopover
            title={t('common.filter')}
            resetLabel={t('common.reset')}
            activeCount={activeFilterCount}
            onReset={resetFilters}
          >
            <DataTableFilterField label={t('common.status')}>
              <Select
                value={statusFilter}
                onValueChange={(v: StatusFilter) => {
                  setStatusFilter(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={VOUCHER_STATUS_FILTER.ALL}>{t('common.all')}</SelectItem>
                  <SelectItem value={VOUCHER_STATUS_FILTER.AVAILABLE}>
                    {t('voucher.status.available')}
                  </SelectItem>
                  <SelectItem value={VOUCHER_STATUS_FILTER.UNAVAILABLE}>
                    {t('voucher.status.unavailable')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </DataTableFilterField>

            <DataTableFilterField label={t('common.type')}>
              <Select
                value={typeFilter}
                onValueChange={(v: TypeFilter) => {
                  setTypeFilter(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={VOUCHER_TYPE_FILTER.ALL}>{t('common.all')}</SelectItem>
                  <SelectItem value={VOUCHER_TYPE_FILTER.TOUR}>{t('voucher.type.tour')}</SelectItem>
                  <SelectItem value={VOUCHER_TYPE_FILTER.POI}>{t('voucher.type.poi')}</SelectItem>
                </SelectContent>
              </Select>
            </DataTableFilterField>
          </FilterPopover>
        </div>
      </DataTableToolbar>

      <VoucherListTable
        vouchers={vouchers}
        loading={isTenantResolving || (currentTenantId ? isLoading : false)}
        pagination={{ current: page, total, pageSize }}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage)
          setPageSize(nextPageSize)
        }}
        onEdit={handleEdit}
      />
    </PageContainer>
  )
}
